<template>
  <!-- Compact is a card's width - see `compact`. The markup stays the table's: each
       cell carries its column's name in `data-label`, and the styles stack the cells
       of an activation under each other with that name in front of them. -->
  <div
    class="sensor-activations-table"
    :class="{ 'sensor-activations-table--compact': compact }"
  >
    <table>
      <!-- Widths shared out over the whole row, so the columns fill it rather than
           leaving the space past the last of them empty. The address is what runs
           long, so it gets the most. -->
      <colgroup v-if="!compact">
        <col class="sensor-activations-table__col">
        <col class="sensor-activations-table__col">
        <col class="sensor-activations-table__col">
        <col class="sensor-activations-table__col--location">
        <col class="sensor-activations-table__col">
      </colgroup>
      <thead>
        <tr>
          <th>{{ $t('satellite-tracking/report.sensor_activated') }}</th>
          <th>{{ $t('satellite-tracking/report.sensor_deactivated') }}</th>
          <th>{{ $t('satellite-tracking/report.total_sensor_activity_time') }}</th>
          <th>{{ $t('satellite-tracking/report.location_name') }}</th>
          <th class="sensor-activations-table__map-header">
            {{ $t('satellite-tracking/report.map_routes') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(activation, index) in activations"
          :key="index"
        >
          <td :data-label="$t('satellite-tracking/report.sensor_activated')">
            {{ activation.activatedAt }}
          </td>
          <td :data-label="$t('satellite-tracking/report.sensor_deactivated')">
            {{ activation.deactivatedAt }}
          </td>
          <td :data-label="$t('satellite-tracking/report.total_sensor_activity_time')">
            {{ activation.duration }}
          </td>
          <td :data-label="$t('satellite-tracking/report.location_name')">
            <template v-if="hasStartPosition(activation)">
              <!-- Resolved together for every activation of the row - see
                   `resolveAddresses` - rather than one request per activation. -->
              <template v-if="batchReverseGeocode">
                <v-progress-circular
                  v-if="addressesLoading"
                  indeterminate
                  width="2"
                  size="15"
                />
                <span
                  v-else
                  :title="batchAddresses[index] || ''"
                >{{ batchAddresses[index] || '-' }}</span>
              </template>
              <reverse-geocode-preview
                v-else
                :row-data="activation"
                :row-index="index"
                :config="addressConfig"
              />
            </template>
            <span v-else>-</span>
          </td>
          <!-- The whole cell is the button, so the pin does not have to be aimed at. -->
          <td
            class="sensor-activations-table__map-cell"
            :data-label="$t('satellite-tracking/report.map_routes')"
            :title="$t('satellite-tracking/report.map_routes')"
            @click="onShowOnMap(activation)"
          >
            <img
              src="/img/icons/show-on-map-icon.svg"
              alt=""
              width="16"
              height="16"
              class="sensor-activations-table__map-icon"
            >
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import ReverseGeocodePreview from '@/global/components/geocoding/ReverseGeocodePreview.vue'
import { batchReverseGeocode as fetchBatchReverseGeocode } from '@/global/components/geocoding/reverseGeocoding'
import { isFunction, isObject, isSet } from '@/global/utils/utils'

// Addresses already resolved, per list of activations. The expansion is drawn anew each
// time its row is opened, and the addresses of a row do not change between two openings -
// so a row opened again is not asked for again. Keyed by the list itself, so it goes
// when the report's data does.
const resolvedAddressesCache = new WeakMap()

const props = defineProps({
  activations: {
    type: Array,
    default: () => ([])
  },
  onShowRoute: {
    type: Function,
    default: null
  },
  // Whether the report resolves its addresses in batches - what the report itself says,
  // see `batchReverseGeocode` in the report store.
  batchReverseGeocode: {
    type: Boolean,
    default: false
  },
  // Drawn in a narrow space - a grid card - where five columns side by side do not fit.
  compact: {
    type: Boolean,
    default: false
  }
})

const batchAddresses = ref({})
const addressesLoading = ref(false)

const addressConfig = computed(() => ({
  params: ({ data }) => ({
    lat: data?.latitude,
    lng: data?.longitude,
    withUserLocations: true
  })
}))

function hasStartPosition (activation) {
  return isSet(activation?.latitude) && isSet(activation?.longitude)
}

// Every activation of the row in one request, keyed by its place in the list.
async function resolveAddresses (activations) {
  if (resolvedAddressesCache.has(activations)) {
    batchAddresses.value = resolvedAddressesCache.get(activations)
    return
  }

  const coordinates = activations.reduce((result, activation, index) => {
    if (hasStartPosition(activation)) {
      result.push({
        lat: activation.latitude,
        lng: activation.longitude,
        id: index
      })
    }

    return result
  }, [])

  if (!coordinates.length) {
    batchAddresses.value = {}
    return
  }

  addressesLoading.value = true

  try {
    // User locations as well, as the single reverse geocode below asks for them
    const response = await fetchBatchReverseGeocode(coordinates, { withUserLocations: true })
    const addresses = isObject(response) ? response : {}

    // Only a list that is still the one on screen gets the answer.
    if (activations === props.activations) {
      batchAddresses.value = addresses
    }

    resolvedAddressesCache.set(activations, addresses)
  }
  finally {
    if (activations === props.activations) {
      addressesLoading.value = false
    }
  }
}

watch(
  () => [props.activations, props.batchReverseGeocode],
  ([activations, useBatch]) => {
    if (useBatch && Array.isArray(activations)) {
      resolveAddresses(activations)
    }
  },
  { immediate: true }
)

function onShowOnMap (activation) {
  if (isFunction(props.onShowRoute)) {
    props.onShowRoute(activation)
  }
}
</script>

<style scoped lang="scss">
// Where the line running down from the row's arrow stands, measured from the left edge
// of the expansion: under the middle of the arrow in the table's row-controls cell -
// its 10px of padding and half of the 24px control.
$tree-line-left: 22px;
// Where the activations start: past the row-controls cell, so their columns begin
// where the row's own do.
$tree-indent: 44px;
$tree-line-color: #111827;
$row-border-color: #E5E7EB;

.sensor-activations-table {
  padding: 0 0 0 $tree-indent;
  background-color: #F3F4F6;

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  .sensor-activations-table__col {
    width: 16%;
  }

  .sensor-activations-table__col--location {
    width: 36%;
  }

  th,
  td {
    position: relative;
    text-align: left;
    padding: 8px 10px;
    font-family: 'Satoshi', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th {
    height: 36px;
    font-size: 12px;
    color: #6B7280;
    font-weight: 400;
  }

  td {
    height: 32px;
    font-size: 12px;
    color: #111827;
    font-weight: 500;
    border-top: 1px solid $row-border-color;
  }

  // The tree down the left: a line from the row's arrow past the header and every
  // activation, a branch out to each of them, and a rounded turn into the last. Drawn
  // off each row's first cell, so it stretches with the rows whatever their height.
  th:first-child,
  td:first-child {
    overflow: visible;

    &::before {
      content: '';
      position: absolute;
      top: -1px;
      bottom: 0;
      left: -($tree-indent - $tree-line-left);
      border-left: 1px solid $tree-line-color;
    }
  }

  td:first-child::after {
    content: '';
    position: absolute;
    top: 50%;
    left: -($tree-indent - $tree-line-left);
    width: $tree-indent - $tree-line-left;
    border-top: 1px solid $tree-line-color;
  }

  // The turn into the last activation is one stroke - the line coming down and the
  // branch going out drawn by the same border. Two elements meeting at the corner land
  // on different half pixels and the join shows as a break in the line.
  tbody tr:last-child td:first-child {
    &::before {
      bottom: 50%;
      width: $tree-indent - $tree-line-left;
      border-bottom: 1px solid $tree-line-color;
      border-bottom-left-radius: 6px;
    }

    &::after {
      content: none;
    }
  }

  .sensor-activations-table__map-header,
  .sensor-activations-table__map-cell {
    text-align: center;
  }

  .sensor-activations-table__map-cell {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #E5E7EB;
    }
  }
}

/* An activation per card, its cells stacked as label and value the way the grid card
   lays out its own fields. No tree - there is no row above it to hang from. */
.sensor-activations-table--compact {
  padding: 8px 12px;
  background-color: #F9FAFB;

  table {
    table-layout: auto;
  }

  thead {
    display: none;
  }

  table,
  tbody,
  tr,
  td {
    display: block;
    width: 100%;
  }

  tr {
    background-color: #FFFFFF;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    padding: 4px 0;

    & + tr {
      margin-top: 8px;
    }
  }

  td,
  td:first-child {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    height: auto;
    padding: 6px 12px;
    border-top: none;
    font-size: 12px;
    text-align: right;
    white-space: normal;
    word-break: break-word;

    &::before {
      content: attr(data-label);
      position: static;
      flex: none;
      max-width: 50%;
      border: none;
      border-radius: 0;
      color: #6B7280;
      font-weight: 400;
      text-align: left;
    }
  }

  tbody tr:last-child td:first-child::before {
    width: auto;
    border: none;
    border-radius: 0;
  }

  td:first-child::after,
  tbody tr:last-child td:first-child::after {
    content: none;
  }

  .sensor-activations-table__map-cell {
    width: 100%;
    border-radius: 0;
    text-align: right;
  }

  .sensor-activations-table__map-icon {
    margin: 0;
  }
}

.sensor-activations-table__map-icon {
  display: inline-block;
  vertical-align: middle;
  margin: 0 auto;
}
</style>
